import type { SetStateAction } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { objectToSearchParams, searchParamsToObject } from '../Utils/searchParamsUtils'

export function useSearchParamsStatePersistance<T extends {}>(
  initialState: T,
  ignoredKeys: string[] = [],
  explicitKeys: string[] = [],
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsStateRef = useRef<T | undefined>(undefined)
  const [state, setState] = useState<T>(() => {
    const initialStateWithSearchParams = {
      ...initialState,
      ...searchParamsToObject(searchParams, ignoredKeys, explicitKeys),
    } as T
    searchParamsStateRef.current = initialStateWithSearchParams
    return initialStateWithSearchParams
  })
  
  const set = useCallback(
    (newState: SetStateAction<T>) => {
      if (newState instanceof Function) {
        const resolvedState = newState(state)
        setState(resolvedState)
        setSearchParams(objectToSearchParams(resolvedState, ignoredKeys, explicitKeys), {
          replace: true,
        })
      } else {
        setState(newState)
        setSearchParams(objectToSearchParams(newState, ignoredKeys, explicitKeys), {
          replace: true,
        })
      }
    },
    [setState, setSearchParams],
  )

  return [state, set, searchParams] as const
}
